/**
 * DayHeaders component - Renders day name headers (M T W T F S S or S M T W T F S)
 */

import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { dayOfWeekMap } from '../../../../../../constants'

interface DayHeadersProps {
  /** Week start preference: 0 = Sunday, 1 = Monday */
  weekStartsOn: 0 | 1
}

/**
 * DayHeaders generates localized day name abbreviations based on week start preference
 *
 * - If weekStartsOn === 0: S M T W T F S (Sunday first)
 * - If weekStartsOn === 1: M T W T F S S (Monday first)
 */
export const DayHeaders: React.FC<DayHeadersProps> = ({ weekStartsOn }) => {
  const { formatMessage } = useIntl()

  const headers = useMemo(() => {
    const headerArray: string[] = []

    if (weekStartsOn === 0) {
      // Sunday first: S M T W T F S
      for (let i = 0; i < 7; i++) {
        const dayMapping = dayOfWeekMap.find((d) => d.value === i)
        if (dayMapping) {
          const dayName = formatMessage({ id: dayMapping.name })
          headerArray.push(dayName.charAt(0).toUpperCase())
        }
      }
    } else {
      // Monday first: M T W T F S S
      for (let i = 1; i <= 7; i++) {
        const dayIndex = i === 7 ? 0 : i // Wrap around: 1,2,3,4,5,6,0
        const dayMapping = dayOfWeekMap.find((d) => d.value === dayIndex)
        if (dayMapping) {
          const dayName = formatMessage({ id: dayMapping.name })
          headerArray.push(dayName.charAt(0).toUpperCase())
        }
      }
    }

    return headerArray
  }, [weekStartsOn, formatMessage])

  return (
    <div className="mini-calendar-day-headers">
      {headers.map((header, index) => (
        <div key={index} className="mini-calendar-day-header">
          {header}
        </div>
      ))}
    </div>
  )
}
